import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Clock3, FileAudio, FolderOpen, LockKeyhole, Mic, RefreshCw, RotateCcw, Settings, Sparkles, Users } from 'lucide-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  isTerminalStage,
  meetlyApi,
  normalizeMeetlyMeetingStatus,
  type MeetlyMeeting,
  type MeetlyMeetingScope,
  type MeetlyMeetingStatusRaw,
  type MeetlyProcessingStage,
  type MeetlyShareUser,
} from '../../api/meetly';
import MeetlyProcessingTimeline, { type ProcessingProgress } from '../../components/MeetlyProcessingTimeline';
import MeetlyRecorder, { type StoppedRecording } from '../../components/MeetlyRecorder';
import MeetlyResultWorkspace from '../../components/MeetlyResultWorkspace';
import MeetlyInterruptedSession from '../../components/MeetlyInterruptedSession';
import { useAccess } from '../../contexts/AccessContext';
import { readActiveSession, type ActiveSessionRecord } from '../../hooks/useMeetlyChunkedSession';
import { MEETLY_SHARED_PATH } from '../../utils/meetlyShare';
import MeetlyMeetingList from './MeetlyMeetingList';

const isPending = (status: MeetlyMeetingStatusRaw) => {
  const normalizedStatus = normalizeMeetlyMeetingStatus(status);
  return normalizedStatus === 'SUBMITTING' || normalizedStatus === 'QUEUED';
};
const isCaptureRequest = (search: string) => new URLSearchParams(search).get('record') === '1';
/**
 * Fallback poll cadence while a meeting is processing.
 *
 * Realtime is the primary signal now that the worker emits every stage, so this
 * only has to cover a dropped socket. It backs off rather than hammering a fixed
 * 3 seconds, and it does not run at all while the tab is hidden — a backgrounded
 * tab polling a full meeting payload every three seconds for four minutes was pure
 * waste, since the socket wakes the page the moment it is foregrounded again.
 */
const POLL_BACKOFF_MS = [4_000, 8_000, 15_000, 30_000] as const;
const pollDelayFor = (attempt: number) =>
  POLL_BACKOFF_MS[Math.min(attempt, POLL_BACKOFF_MS.length - 1)];

type MeetlyStatusDetail = {
  meetingId?: string;
  status?: MeetlyProcessingStage;
  terminal?: boolean;
  progress?: ProcessingProgress | null;
};

const submissionErrorMessage = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 429) {
    return 'No meeting credits are available this month. Ask an administrator to increase your Meetly allowance.';
  }
  if (status === 503) {
    return 'Meetly processing is not configured on this TeamLio server. Ask an administrator to complete the private Meetly setup.';
  }
  return 'Meetly could not submit this meeting. Keep this page open and try again.';
};

/**
 * Where a meeting is, as a stage. The backend now sends `processing_stage`
 * directly; the fallback keeps the page correct against an older server that only
 * sends the four-value `status`.
 */
const stageOf = (meeting: MeetlyMeeting): MeetlyProcessingStage => {
  if (meeting.processing_stage) return meeting.processing_stage;
  const normalized = normalizeMeetlyMeetingStatus(meeting.status);
  if (normalized === 'COMPLETED') return 'COMPLETED';
  if (normalized === 'FAILED') return 'FAILED';
  if (normalized === 'SUBMITTING') return 'PROCESSING';
  return meeting.has_transcript ? 'SUMMARIZING' : 'TRANSCRIBING';
};

function ProcessingState({
  meeting,
  stage,
  progress,
  transcript,
  isRefreshing,
  readOnly = false,
  onRefresh,
  onRestart,
}: {
  meeting: MeetlyMeeting;
  stage: MeetlyProcessingStage;
  progress: ProcessingProgress | null;
  transcript: string | null;
  isRefreshing: boolean;
  /** A colleague's meeting shared with me: nothing here is mine to restart. */
  readOnly?: boolean;
  onRefresh: () => void;
  onRestart: () => void;
}) {
  if (stage === 'FAILED') {
    return (
      <section
        role="alert"
        className="gt-card mx-auto max-w-2xl overflow-hidden text-center"
        style={{ borderColor: 'rgba(220,38,38,0.22)' }}
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700"><RotateCcw size={21} aria-hidden="true" /></div>
        <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Processing stopped</h2>
        <p className="mt-2 text-sm leading-6 text-[var(--gt-text-secondary)]">
          {readOnly
            ? `Meetly could not finish this meeting. ${meeting.owner_name || 'Its owner'} can record it again.`
            : 'Meetly could not finish this meeting. Review the source and try again.'}
        </p>
        {!readOnly && <p className="mt-4 text-xs text-[var(--gt-text-tertiary)]">Most failures are recoverable by capturing the meeting again.</p>}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-2 text-sm font-semibold text-[var(--gt-text-primary)] hover:bg-[var(--gt-surface-1)] disabled:cursor-wait disabled:opacity-60"
          >
            <RefreshCw className={isRefreshing ? 'animate-spin motion-reduce:animate-none' : undefined} size={14} aria-hidden="true" />
            {isRefreshing ? 'Checking…' : 'Re-check status'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={onRestart}
              className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white"
            >
              Start new recording
            </button>
          )}
        </div>
      </section>
    );
  }

  const transcriptReady = stage === 'TRANSCRIPT_READY' || stage === 'SUMMARIZING';
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <section className="gt-card overflow-hidden" aria-live="polite">
        <div className="border-b border-[var(--gt-divider)] bg-[var(--gt-surface-1)] px-6 py-4">
          <span className="inline-flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-[0.16em] text-[var(--gt-text-secondary)]">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#e66a52] motion-reduce:animate-none" />
            {transcriptReady ? 'Almost ready' : 'Processing'}
          </span>
        </div>
        <div className="p-7 sm:p-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
            {stage === 'PROCESSING' ? <LockKeyhole size={26} aria-hidden="true" /> : <Sparkles size={26} aria-hidden="true" />}
          </div>
          <h2 className="gt-display mt-5 text-center text-2xl text-[var(--gt-text-primary)]">
            {transcriptReady ? 'Writing the summary' : readOnly ? 'This meeting is still processing' : 'Processing your meeting'}
          </h2>
          <p className="mx-auto mt-3 max-w-md text-center text-sm leading-6 text-[var(--gt-text-secondary)]">
            {transcriptReady
              ? 'The transcript is ready to read below. The summary and action items land in a moment.'
              : 'You can leave this page and come back — nothing stops if you navigate away.'}
          </p>

          {/* Replaces the old three-item static list, which showed the same two
              ticks for the whole wait no matter what was actually happening. */}
          <MeetlyProcessingTimeline
            className="mx-auto mt-8 max-w-sm"
            stage={stage}
            progress={progress}
            error={meeting.error}
            hasTranscript={Boolean(meeting.has_transcript || transcript)}
          />

          <div className="mx-auto mt-7 flex justify-center">
            <button type="button" onClick={onRefresh} disabled={isRefreshing} className="inline-flex min-h-9 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-xs font-semibold text-[var(--gt-text-secondary)] hover:bg-[var(--gt-surface-1)] disabled:cursor-wait disabled:opacity-60">
              <RefreshCw className={isRefreshing ? 'animate-spin motion-reduce:animate-none' : undefined} size={13} aria-hidden="true" />
              {isRefreshing ? 'Checking…' : 'Re-check status'}
            </button>
          </div>
        </div>
      </section>

      {/* The transcript exists a long time before the summary does. Holding it
          back until the summary lands was the single biggest source of dead
          waiting in the old UI. */}
      {transcript && (
        <section className="gt-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--gt-divider)] bg-[var(--gt-surface-1)] px-6 py-4">
            <h3 className="text-sm font-semibold text-[var(--gt-text-primary)]">Transcript</h3>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--gt-text-tertiary)]">Ready</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto px-6 py-5">
            <p className="whitespace-pre-wrap text-sm leading-7 text-[var(--gt-text-secondary)]">{transcript}</p>
          </div>
        </section>
      )}
    </div>
  );
}

export default function MeetlyWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { meetingId } = useParams<{ meetingId: string }>();
  const { hasAccess, isPanelVisible } = useAccess();
  // Recording, uploads and Usage need Meetly; a colleague without it only ever
  // gets "Shared with me". The API 403s their own list, which also covers an
  // account whose Access claim this page cannot see (no profile).
  const [meetlyDenied, setMeetlyDenied] = useState(false);
  const canRecord = (!hasAccess || isPanelVisible('meetly')) && !meetlyDenied;
  const sharedView = !meetingId && location.pathname === MEETLY_SHARED_PATH;
  const listScope: MeetlyMeetingScope = sharedView ? 'shared' : 'mine';
  const redirectToShared = !meetingId && !sharedView && !canRecord;
  const captureMode = !meetingId && !sharedView && isCaptureRequest(location.search);
  const [meetings, setMeetings] = useState<MeetlyMeeting[]>([]);
  const [landingLoading, setLandingLoading] = useState(!meetingId && !captureMode);
  const [listFailed, setListFailed] = useState(false);
  const [listReloadToken, setListReloadToken] = useState(0);
  const [recording, setRecording] = useState<StoppedRecording | null>(null);
  const [meeting, setMeeting] = useState<MeetlyMeeting | null>(null);
  const [loadedMeetingId, setLoadedMeetingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(meetingId));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importingAudio, setImportingAudio] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  // Stage and counts arrive over the socket between fetches, so they are held
  // separately from `meeting` — updating them must not mean refetching it.
  const [liveStage, setLiveStage] = useState<MeetlyProcessingStage | null>(null);
  const [liveProgress, setLiveProgress] = useState<ProcessingProgress | null>(null);
  const [earlyTranscript, setEarlyTranscript] = useState<string | null>(null);
  // Read once at mount: a session left behind by a page that went away mid-meeting.
  const [interruptedSession, setInterruptedSession] = useState<ActiveSessionRecord | null>(
    () => readActiveSession(),
  );
  const mountedRef = useRef(true);
  const earlyTranscriptRef = useRef<string | null>(null);
  const importAudioInputRef = useRef<HTMLInputElement>(null);
  const requestGenerationRef = useRef(0);
  const requestInFlightRef = useRef<number | null>(null);
  const pendingMeetingGenerationRef = useRef<number | null>(null);
  const pendingTerminalRefreshRef = useRef<number | null>(null);
  const terminalRefreshRequestedRef = useRef<number | null>(null);
  const requestMeetingRef = useRef<((refreshing: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Only the landing view (no meeting id, not capturing) renders a list, so
  // that's the only case that needs to fetch one.
  useEffect(() => {
    if (captureMode || meetingId || redirectToShared) {
      setLandingLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLandingLoading(true);

    const loadMeetings = async () => {
      try {
        const response = await meetlyApi.listMeetings({ page: 1, page_size: 50, scope: listScope });
        if (cancelled) return;
        const loaded = response.data.meetings || [];
        // An API older than Meeting → Share ignores `scope` and returns the
        // caller's OWN meetings; only rows it marks as shared belong here.
        setMeetings(listScope === 'shared' ? loaded.filter(item => item.is_shared) : loaded);
        setListFailed(false);
      } catch (error) {
        if (cancelled) return;
        // No Meetly on this account: its own list is off limits, but what
        // colleagues shared with it is not.
        if (listScope === 'mine' && (error as { response?: { status?: number } })?.response?.status === 403) {
          setMeetlyDenied(true);
          return;
        }
        // Previously this fabricated a placeholder meeting, which made a failed
        // request look like an empty but working workspace.
        setMeetings([]);
        setListFailed(true);
      } finally {
        if (!cancelled) setLandingLoading(false);
      }
    };

    void loadMeetings();
    return () => {
      cancelled = true;
    };
  }, [captureMode, meetingId, listReloadToken, listScope, redirectToShared]);

  const hasMeetings = meetings.length > 0;

  const reloadMeetings = useCallback(() => setListReloadToken(token => token + 1), []);

  const reloadMeetingAndList = useCallback(() => {
    reloadMeetings();
    void requestMeetingRef.current?.(false);
  }, [reloadMeetings]);

  const updateMeeting = useCallback(async (id: string, update: Parameters<typeof meetlyApi.updateMeeting>[1]) => {
    await meetlyApi.updateMeeting(id, update);
    if (mountedRef.current) reloadMeetings();
  }, [reloadMeetings]);

  const deleteMeeting = useCallback(async (id: string) => {
    await meetlyApi.deleteMeeting(id);
    if (mountedRef.current) reloadMeetings();
  }, [reloadMeetings]);

  const markShared = useCallback((id: string, sharedWith: MeetlyShareUser[]) => {
    setMeetings(current => current.map(item => (item.id === id ? { ...item, shared_with: sharedWith } : item)));
  }, []);

  /**
   * Pull the transcript the moment the server says it exists.
   *
   * Guarded by a ref rather than the state it sets: several TRANSCRIPT_READY
   * frames can arrive (a reconnect replays one), and each would otherwise start
   * its own fetch of the same text.
   */
  const loadEarlyTranscript = useCallback(async (id: string) => {
    if (earlyTranscriptRef.current === id) return;
    earlyTranscriptRef.current = id;
    try {
      const response = await meetlyApi.getTranscript(id);
      if (!mountedRef.current) return;
      setEarlyTranscript(response.data.transcript || null);
    } catch {
      // The summary view fetches it again later. A failure here costs the early
      // preview, not the transcript.
      earlyTranscriptRef.current = null;
    }
  }, []);

  useEffect(() => {
    setMeeting(null);
    setLoadedMeetingId(null);
    setRequestError(null);
    setIsRefreshing(false);
    setLiveStage(null);
    setLiveProgress(null);
    setEarlyTranscript(null);
    earlyTranscriptRef.current = null;
    if (!meetingId) {
      setLoading(false);
      requestMeetingRef.current = null;
      pendingMeetingGenerationRef.current = null;
      pendingTerminalRefreshRef.current = null;
      terminalRefreshRequestedRef.current = null;
      return undefined;
    }
    // Lio-Notes owns the current Meetly data. Old Teamlio's UUID links cannot
    // exist there, so return visitors to the current meeting list instead of
    // showing a misleading connection error.
    if (!meetingId.startsWith('meeting-')) {
      setLoading(false);
      requestMeetingRef.current = null;
      pendingMeetingGenerationRef.current = null;
      pendingTerminalRefreshRef.current = null;
      terminalRefreshRequestedRef.current = null;
      navigate('/meetly', { replace: true });
      return undefined;
    }
    setLoading(true);
    let disposed = false;
    let pendingRefreshTimer: number | null = null;
    // Reset on every stage change, so a meeting that is visibly moving is checked
    // promptly and a stuck one is checked rarely.
    let pollAttempt = 0;
    let lastStage: MeetlyProcessingStage | null = null;
    const requestGeneration = ++requestGenerationRef.current;
    // An unresolved initial request is eligible for a terminal event. Once a
    // response is known terminal this generation is removed from eligibility.
    pendingMeetingGenerationRef.current = requestGeneration;

    const clearPendingRefresh = () => {
      if (pendingRefreshTimer === null) return;
      window.clearTimeout(pendingRefreshTimer);
      pendingRefreshTimer = null;
    };

    const schedulePendingRefresh = () => {
      if (disposed
        || requestGeneration !== requestGenerationRef.current
        || pendingRefreshTimer !== null) return;
      // A hidden tab schedules nothing. The visibilitychange handler below picks
      // the loop back up, and a stage frame over the socket does too, so a tab
      // left open in the background costs no requests at all.
      if (typeof document !== 'undefined' && document.hidden) return;
      const delay = pollDelayFor(pollAttempt);
      pollAttempt += 1;
      pendingRefreshTimer = window.setTimeout(() => {
        pendingRefreshTimer = null;
        void requestMeeting(false);
      }, delay);
    };

    const requestMeeting = async (refreshing: boolean) => {
      if (disposed
        || requestGeneration !== requestGenerationRef.current
        || requestInFlightRef.current === requestGeneration) return;
      requestInFlightRef.current = requestGeneration;
      if (refreshing) setIsRefreshing(true);
      try {
        const response = await meetlyApi.getMeeting(meetingId);
        if (disposed || requestGeneration !== requestGenerationRef.current) return;
        const loaded = response.data.meeting;
        setMeeting(loaded);
        const nextStage = stageOf(loaded);
        if (nextStage !== lastStage) {
          lastStage = nextStage;
          pollAttempt = 0;
          setLiveStage(current => (current === nextStage ? current : nextStage));
        }
        if (isPending(loaded.status)) {
          pendingMeetingGenerationRef.current = requestGeneration;
          schedulePendingRefresh();
        } else if (pendingMeetingGenerationRef.current === requestGeneration) {
          pendingMeetingGenerationRef.current = null;
          clearPendingRefresh();
        }
        setLoadedMeetingId(meetingId);
        setRequestError(null);
        setLoading(false);
      } catch (error) {
        if (disposed || requestGeneration !== requestGenerationRef.current) return;
        setRequestError('Meetly could not load this meeting. Check your connection and try again.');
        setLoading(false);
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (pendingMeetingGenerationRef.current === requestGeneration && status !== 403 && status !== 404) {
          schedulePendingRefresh();
        }
      } finally {
        if (requestInFlightRef.current === requestGeneration) {
          requestInFlightRef.current = null;
        }
        if (!disposed
          && requestGeneration === requestGenerationRef.current
          && pendingTerminalRefreshRef.current === requestGeneration) {
          pendingTerminalRefreshRef.current = null;
          void requestMeeting(true);
        } else if (!disposed && requestGeneration === requestGenerationRef.current) {
          setIsRefreshing(false);
        }
      }
    };

    requestMeetingRef.current = requestMeeting;
    const handleStatusChange = (event: Event) => {
      const detail = (event as CustomEvent<MeetlyStatusDetail>).detail;
      if (detail?.meetingId !== meetingId
        || requestGeneration !== requestGenerationRef.current) return;

      // Non-terminal stages are rendered straight from the frame. Refetching the
      // whole meeting to learn "still transcribing" is exactly the round trip the
      // progress stream exists to avoid.
      if (detail.status && !isTerminalStage(detail.status)) {
        lastStage = detail.status;
        pollAttempt = 0;
        setLiveStage(detail.status);
        if (detail.progress) setLiveProgress(detail.progress);
        // The transcript becomes readable here, well before the summary.
        if (detail.status === 'TRANSCRIPT_READY') void loadEarlyTranscript(meetingId);
        // Reschedule the fallback from now rather than leaving an old short timer
        // to fire on top of a frame that already told us the answer.
        clearPendingRefresh();
        schedulePendingRefresh();
        return;
      }

      if (pendingMeetingGenerationRef.current !== requestGeneration
        || terminalRefreshRequestedRef.current === requestGeneration) return;

      terminalRefreshRequestedRef.current = requestGeneration;
      if (requestInFlightRef.current === requestGeneration) {
        pendingTerminalRefreshRef.current = requestGeneration;
        setIsRefreshing(true);
        return;
      }
      void requestMeeting(true);
    };

    const handleVisibility = () => {
      if (document.hidden || disposed) return;
      if (pendingMeetingGenerationRef.current !== requestGeneration) return;
      // Back in view after not polling: check once immediately, then resume the
      // ladder from the top.
      pollAttempt = 0;
      void requestMeeting(false);
    };

    window.addEventListener('meetly-status-changed', handleStatusChange);
    document.addEventListener('visibilitychange', handleVisibility);
    // A microtask lets StrictMode's probe cleanup cancel its provisional effect
    // before network work begins, without introducing timer-based polling.
    void Promise.resolve().then(() => requestMeeting(false));
    return () => {
      disposed = true;
      window.removeEventListener('meetly-status-changed', handleStatusChange);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (requestMeetingRef.current === requestMeeting) requestMeetingRef.current = null;
      if (requestInFlightRef.current === requestGeneration) requestInFlightRef.current = null;
      if (pendingMeetingGenerationRef.current === requestGeneration) pendingMeetingGenerationRef.current = null;
      if (pendingTerminalRefreshRef.current === requestGeneration) pendingTerminalRefreshRef.current = null;
      if (terminalRefreshRequestedRef.current === requestGeneration) terminalRefreshRequestedRef.current = null;
      clearPendingRefresh();
      if (requestGeneration === requestGenerationRef.current) {
        requestGenerationRef.current += 1;
      }
    };
  }, [meetingId, navigate]);

  const processMeeting = async () => {
    if (!recording || submitting) return;
    setSubmitting(true);
    setRequestError(null);
    try {
      const response = await meetlyApi.createMeeting(recording.file, {
        language: 'en',
        summary_template: 'project_sync',
        duration_seconds: recording.durationSeconds,
        capture_mode: recording.captureMode,
      });
      if (!mountedRef.current) return;
      setRecording(null);
      navigate(`/meetly/meetings/${response.data.meeting_id}`);
    } catch (error) {
      if (!mountedRef.current) return;
      setRequestError(submissionErrorMessage(error));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const importAudio = async (file: File) => {
    if (importingAudio) return;
    setImportingAudio(true);
    setRequestError(null);
    try {
      const response = await meetlyApi.createMeeting(file, {
        language: 'en',
        summary_template: 'project_sync',
        capture_mode: 'imported',
      });
      if (!mountedRef.current) return;
      navigate(`/meetly/meetings/${response.data.meeting_id}`);
    } catch (error) {
      if (mountedRef.current) setRequestError(submissionErrorMessage(error));
    } finally {
      if (mountedRef.current) setImportingAudio(false);
    }
  };

  const currentMeeting = loadedMeetingId === meetingId ? meeting : null;

  const isLanding = !meetingId && !captureMode;
  const showNewRecordingButton = isLanding && !sharedView && !landingLoading && !listFailed && hasMeetings;
  // Usage & limits is the only route to the credits screen, so it must NOT ride
  // on the meeting list: an admin setting up everyone's monthly meetings
  // usually has no meetings of their own, and a failed list would otherwise
  // strand them with no way in.
  const showUsageLink = isLanding && !sharedView && !landingLoading;

  if (redirectToShared) return <Navigate to={MEETLY_SHARED_PATH} replace />;

  return (
    <main className="gt-shell min-h-screen">
      <div className="mx-auto w-full max-w-[1700px] p-4 md:p-7">
        {isLanding && (
          <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="gt-eyebrow">Task management / Meetly</p>
              <h1 className="gt-display mt-1 text-3xl text-[var(--gt-text-primary)]">Meeting workspace</h1>
              <p className="mt-2 text-sm text-[var(--gt-text-secondary)]">
                {sharedView
                  ? 'Meetings your colleagues shared with you. You can read them, not change them.'
                  : 'Capture conversations, review summaries, and turn decisions into tasks.'}
              </p>
            </div>
            {(showUsageLink || showNewRecordingButton) && (
              <div className="flex flex-wrap items-center gap-3 self-start sm:self-auto">
                {showUsageLink && <button type="button" onClick={() => navigate('/meetly/usage')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><Settings size={16} aria-hidden="true" /> Usage & limits</button>}
                {showNewRecordingButton && <>
                <button type="button" onClick={() => navigate('/meetly?record=1')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><FolderOpen size={16} aria-hidden="true" /> Recording</button>
                <button type="button" onClick={() => importAudioInputRef.current?.click()} disabled={importingAudio} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"><FileAudio size={16} aria-hidden="true" /> {importingAudio ? 'Uploading…' : 'Upload audio'}</button>
                <button type="button" onClick={() => navigate('/meetly?record=1')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><ArrowLeft size={16} aria-hidden="true" /> New recording</button>
                </>}
              </div>
            )}
          </header>
        )}

        {isLanding && canRecord && (
          <div role="tablist" aria-label="Meetings" className="mb-5 inline-flex rounded-full border border-[var(--gt-border)] bg-[var(--gt-surface-1)] p-1">
            {([['mine', 'My meetings'], ['shared', 'Shared with me']] as const).map(([scope, label]) => (
              <button
                key={scope}
                type="button"
                role="tab"
                aria-selected={listScope === scope}
                onClick={() => navigate(scope === 'shared' ? MEETLY_SHARED_PATH : '/meetly')}
                className={`inline-flex min-h-9 items-center gap-1.5 rounded-full px-4 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] ${listScope === scope ? 'bg-white text-[var(--gt-text-primary)] shadow-sm' : 'text-[var(--gt-text-secondary)] hover:text-[var(--gt-text-primary)]'}`}
              >
                {scope === 'shared' && <Users size={14} aria-hidden="true" />}
                {label}
              </button>
            ))}
          </div>
        )}

        {isLanding && requestError && <div role="alert" className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">{requestError}</div>}
        {isLanding && (
          <input
            ref={importAudioInputRef}
            type="file"
            accept="audio/*,video/*"
            className="hidden"
            aria-label="Import audio file"
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importAudio(file);
            }}
          />
        )}

        {/* A recording whose page went away is recoverable now that its audio is
            already on the server. Shown above everything else on the landing view,
            because it is the only thing there that is time-sensitive. */}
        {isLanding && !sharedView && interruptedSession && (
          <div className="mb-6">
            <MeetlyInterruptedSession
              session={interruptedSession}
              onResumed={(id) => {
                setInterruptedSession(null);
                navigate(`/meetly/meetings/${id}`);
              }}
              onDiscarded={() => setInterruptedSession(null)}
            />
          </div>
        )}

        {isLanding ? (
          landingLoading ? (
            <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center" aria-live="polite">
              <Clock3 className="text-[var(--gt-primary)]" size={28} aria-hidden="true" />
              <p className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Opening private workspace…</p>
            </section>
          ) : listFailed ? (
            <section role="alert" className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
                <RotateCcw size={21} aria-hidden="true" />
              </span>
              <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Could not load meetings</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
                Meetly could not reach the meeting service. Check your connection and try again.
              </p>
              <button
                type="button"
                onClick={reloadMeetings}
                className="mt-5 inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
              >
                Try again
              </button>
            </section>
          ) : !hasMeetings && sharedView ? (
            <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
                <Users size={23} aria-hidden="true" />
              </span>
              <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Nothing shared with you yet</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
                When a colleague shares a meeting with you, it shows up here to read.
              </p>
            </section>
          ) : !hasMeetings ? (
            <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
                <Sparkles size={23} aria-hidden="true" />
              </span>
              <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">No meetings yet</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
                Record a meeting or upload an audio file. Meetly will turn it into a summary and action items.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/meetly?record=1')}
                  className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                >
                  <Mic size={16} aria-hidden="true" /> Start Recording
                </button>
                <button
                  type="button"
                  onClick={() => importAudioInputRef.current?.click()}
                  disabled={importingAudio}
                  className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-2 text-sm font-bold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FileAudio size={16} aria-hidden="true" /> {importingAudio ? 'Uploading…' : 'Upload audio'}
                </button>
              </div>
            </section>
          ) : (
            <MeetlyMeetingList
              meetings={meetings}
              scope={listScope}
              onSelect={id => navigate(`/meetly/meetings/${encodeURIComponent(id)}`)}
              onUpdate={updateMeeting}
              onDelete={deleteMeeting}
              onShared={markShared}
            />
          )
        ) : (
          <>
            {requestError && <div role="alert" className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">{requestError}</div>}

            {meetingId ? (
              loading ? (
                <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center" aria-live="polite">
                  <Clock3 className="text-[var(--gt-primary)]" size={28} aria-hidden="true" />
                  <p className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Opening private workspace…</p>
                </section>
              ) : currentMeeting ? (
                  normalizeMeetlyMeetingStatus(currentMeeting.status) === 'COMPLETED'
                  ? (
                    <MeetlyResultWorkspace
                      meeting={currentMeeting}
                      onMeetingChanged={reloadMeetingAndList}
                    />
                  )
                  : (
                    <ProcessingState
                      meeting={currentMeeting}
                      stage={liveStage ?? stageOf(currentMeeting)}
                      progress={liveProgress}
                      transcript={earlyTranscript}
                      isRefreshing={isRefreshing}
                      readOnly={Boolean(currentMeeting.is_shared)}
                      onRestart={() => navigate('/meetly?record=1')}
                      onRefresh={() => void requestMeetingRef.current?.(true)}
                    />
                  )
              ) : null
            ) : (
              <>
                <MeetlyRecorder
                  onRecordingReady={setRecording}
                  onRecordingInvalidated={() => setRecording(null)}
                  // The chunked path has already uploaded and mostly transcribed
                  // the meeting by the time recording stops, so there is nothing
                  // left for the user to press "Process meeting" for.
                  onLiveMeetingReady={(id) => navigate(`/meetly/meetings/${id}`)}
                  disabled={submitting}
                />
                {recording && (
                  <section className="mt-5 flex flex-col gap-4 rounded-[20px] border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] p-5 sm:flex-row sm:items-center sm:justify-between" aria-live="polite">
                    <div>
                      <p className="text-sm font-bold text-[var(--gt-primary)]">Capture ready</p>
                      <p className="mt-1 text-xs leading-5 text-[var(--gt-text-secondary)]">Process it privately to generate the summary and action items.</p>
                    </div>
                    <button type="button" onClick={() => void processMeeting()} disabled={submitting} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--gt-primary)] px-6 py-3 text-sm font-bold text-white transition hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60">
                      <Sparkles size={17} aria-hidden="true" /> {submitting ? 'Submitting…' : 'Process meeting'}
                    </button>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
